import { useUserMe } from "~/components/user/composables/useUser";
import type { ConsultantKpi, ConsultantKpiPeriod } from "@asap/shared/schemas/team-member-data";
import { formatDateForDB } from "@asap/shared/utils/date";
import { mapConsultantKpiToViewModel } from "~/components/consultant-kpi/map-consultant-kpi";
import { createMockConsultantKpi } from "~/components/consultant-kpi/mock-consultant-kpi";
import { useApi } from "~/composables/useApi";

export const useConsultantKpi = () => {
  const { invoke } = useApi();
  const { teamMember, isDeveloper } = useUserMe();

  const period = ref<ConsultantKpiPeriod>("month");
  const referenceDate = ref(new Date());
  const selectedTeamMemberId = ref<string | undefined>(undefined);
  const useMockData = ref(false);
  const kpi = ref<ConsultantKpi | null>(null);
  const lastSyncedAt = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  let requestId = 0;

  watch(
    () => teamMember.value?.id,
    (currentTeamMemberId) => {
      if (!currentTeamMemberId || selectedTeamMemberId.value) return;
      selectedTeamMemberId.value = currentTeamMemberId;
    },
    { immediate: true }
  );

  watch(period, () => {
    referenceDate.value = new Date();
  });

  const fetchLastSyncedAt = async (teamMemberId: string) => {
    const response = await invoke({
      path: "team-member-data",
      method: "GET",
      query: {
        teamMemberIds: [teamMemberId],
        type: "consultant_kpi",
      },
    });

    if (response.success) {
      lastSyncedAt.value = response.data.items.at(0)?.created_at ?? null;
    }
  };

  const fetchKpi = async (teamMemberId: string, requestedPeriod: ConsultantKpiPeriod, requestedReferenceDate: Date) => {
    const response = await invoke({
      path: "team-member-data/consultant-kpi",
      method: "GET",
      query: {
        teamMemberId,
        period: requestedPeriod,
        referenceDate: formatDateForDB(requestedReferenceDate),
      },
    });

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return response.data;
  };

  const refresh = async () => {
    const teamMemberId = selectedTeamMemberId.value;
    if (!teamMemberId) return;

    const currentRequestId = ++requestId;
    loading.value = true;
    error.value = null;

    try {
      const [nextKpi] = await Promise.all([
        fetchKpi(teamMemberId, period.value, referenceDate.value),
        fetchLastSyncedAt(teamMemberId),
      ]);
      if (currentRequestId !== requestId) return;

      kpi.value = nextKpi;
    } catch (fetchError) {
      if (currentRequestId !== requestId) return;

      if (!kpi.value) {
        error.value = fetchError instanceof Error ? fetchError.message : "Impossible de récupérer les KPI consultant.";
      }
    } finally {
      if (currentRequestId === requestId) {
        loading.value = false;
      }
    }
  };

  const applyMockData = () => {
    requestId += 1;
    kpi.value = createMockConsultantKpi(period.value);
    lastSyncedAt.value = null;
    loading.value = false;
    error.value = null;
  };

  const onKpiSynced = (syncedAt: string) => {
    lastSyncedAt.value = syncedAt;
    refresh();
  };

  watch(
    [selectedTeamMemberId, period, () => formatDateForDB(referenceDate.value), useMockData],
    () => {
      if (isDeveloper.value && useMockData.value) {
        applyMockData();
        return;
      }

      refresh();
    },
    { immediate: true }
  );

  const viewModel = computed(() => {
    if (!kpi.value) return null;

    return mapConsultantKpiToViewModel(kpi.value, kpi.value.objectives);
  });

  return {
    period,
    referenceDate,
    selectedTeamMemberId,
    loading,
    error,
    refresh,
    viewModel,
    useMockData,
    lastSyncedAt,
    onKpiSynced,
  };
};
